import React, { useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";

const DEFAULT_LICENSE_FEATURES = {
  allow_builder_deploy: false,
  allow_tire_projects: false,
  is_test_license: false,
};

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required("Vorname is required"),
  last_name: Yup.string().required("Nachname is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  company: Yup.string().required("Firma is required"),
  expiry_days: Yup.number()
    .positive("Must be positive")
    .integer("Must be an integer")
    .required("Expiry days is required"),
  max_activations: Yup.number()
    .positive("Must be positive")
    .integer("Must be an integer")
    .required("Max activations is required"),
  product: Yup.string().required("Product is required"),
  type: Yup.string().required("License type is required"),
});

const CreateLicense = () => {
  const [createdLicense, setCreatedLicense] = useState(null);
  const [error, setError] = useState("");

  const initialValues = {
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    expiry_days: 365,
    max_activations: 1,
    product: "profundus-qa",
    type: "subscription",
    features: { ...DEFAULT_LICENSE_FEATURES },
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setError("");
      setCreatedLicense(null);

      const response = await axios.post("/api/licenses", values);
      setCreatedLicense(response.data);
      resetForm();
    } catch (error) {
      console.error("Error creating license:", error);
      if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
        // The axios interceptor will handle the logout
      } else {
        setError(
          error.response?.data?.message ||
            "Failed to create license. Please try again later."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <h2 className="my-4">Create New License</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {createdLicense && (
        <Alert variant="success" className="mb-4">
          <Alert.Heading>License Created Successfully!</Alert.Heading>
          <p>
            A new license has been generated for{" "}
            <strong>{createdLicense.license_key}</strong>.
          </p>
          <hr />
          <p className="mb-0">
            License Key:{" "}
            <code className="p-1 bg-light">{createdLicense.license_key}</code>
          </p>
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Vorname</Form.Label>
                      <Form.Control
                        type="text"
                        name="first_name"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.first_name && errors.first_name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.first_name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nachname</Form.Label>
                      <Form.Control
                        type="text"
                        name="last_name"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.last_name && errors.last_name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.last_name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>E-Mail-Adresse</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Firma</Form.Label>
                      <Form.Control
                        type="text"
                        name="company"
                        value={values.company}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.company && errors.company}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.company}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Expiry Days</Form.Label>
                      <Form.Control
                        type="number"
                        name="expiry_days"
                        value={values.expiry_days}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.expiry_days && errors.expiry_days}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.expiry_days}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Number of days until the license expires
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Maximum Activations</Form.Label>
                      <Form.Control
                        type="number"
                        name="max_activations"
                        value={values.max_activations}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.max_activations && errors.max_activations
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.max_activations}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Maximum number of devices the license can be activated
                        on
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Product</Form.Label>
                      <Form.Select
                        name="product"
                        value={values.product}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.product && errors.product}
                      >
                        <option value="profundus-qa">Profundus QA</option>
                        <option value="profundus-premium">
                          Profundus Premium
                        </option>
                        <option value="profundus-enterprise">
                          Profundus Enterprise
                        </option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.product}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>License Type</Form.Label>
                      <Form.Select
                        name="type"
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.type && errors.type}
                      >
                        <option value="subscription">Subscription</option>
                        <option value="perpetual">Perpetual</option>
                        <option value="trial">Trial</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.type}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Feature Flags</Form.Label>
                      <Form.Check
                        type="switch"
                        id="create-feature-allow-builder-deploy"
                        label="Allow Builder Deploy"
                        checked={
                          values.features?.allow_builder_deploy ??
                          DEFAULT_LICENSE_FEATURES.allow_builder_deploy
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "features.allow_builder_deploy",
                            e.target.checked
                          )
                        }
                      />
                      <Form.Text className="text-muted">
                        Enable deployments that originate from the builder for
                        this license.
                      </Form.Text>
                      <Form.Check
                        type="switch"
                        id="create-feature-allow-tire-projects"
                        label="Allow Tire Projects"
                        className="mt-3"
                        checked={
                          values.features?.allow_tire_projects ??
                          DEFAULT_LICENSE_FEATURES.allow_tire_projects
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "features.allow_tire_projects",
                            e.target.checked
                          )
                        }
                      />
                      <Form.Text className="text-muted">
                        Allow this license to create and open tire projects.
                      </Form.Text>
                      <Form.Check
                        type="switch"
                        id="create-feature-is-test-license"
                        label="Test License"
                        className="mt-3"
                        checked={
                          values.features?.is_test_license ??
                          DEFAULT_LICENSE_FEATURES.is_test_license
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "features.is_test_license",
                            e.target.checked
                          )
                        }
                      />
                      <Form.Text className="text-muted">
                        Mark this license as a test license for clients that
                        need separate test behavior.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-3"
                >
                  {isSubmitting ? "Creating..." : "Create License"}
                </Button>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CreateLicense;
